/**
 * runFlow / resumeFlow — CLI entry helpers for the flow connector.
 *
 * Moved from `runner/src/runner.ts` to `factory-assets/connectors/flow/run-flow.ts`
 * in Phase 3 of the flow-connector extraction (2026-05-08). Behaviour is
 * byte-identical to the previous runner-side implementation; only the log
 * source changes from `runner:flow:<runId>` to `flow-connector:run:<runId>`.
 *
 * The runner re-exports these symbols with a deprecation warning for one
 * release. CLI consumers should switch to
 * `import { runFlow, resumeFlow } from "@skaile/workspaces/factory-assets/connectors/flow/run-flow"`.
 *
 * Internal runner helpers (FlowOrchestrator, bootstrapRunnerLogStore,
 * createAgentSession, resolveDriverPaths, session helpers) are loaded lazily
 * via dynamic `await import("@skaile/workspaces/runner")` inside
 * `loadRunnerModule()` below. The lazy load avoids a static package-level
 * cycle (runner → connectors → factory-assets → runner) — the cycle exists at
 * the workspace level but never resolves at module-load time because
 * factory-assets only ever calls `import` from inside an async function body.
 * The string-literal specifier preserves `bun --compile` and tsup static
 * analysis.
 */

import fs from "node:fs";
import path from "node:path";
import type { AgentEvent } from "@skaile/workspaces/bridge";
import {
  isValidAssetName,
  resolveAgentDir,
  resolveApiKey,
  resolveSettings,
  resolveSkWorkspaceConfig,
} from "@skaile/workspaces/core";
import { createLogger } from "@skaile/workspaces/core/logging";
import { parseSkillFrontmatter, validateFlowVersions } from "@skaile/workspaces/resolver";
import { initTelemetry } from "@skaile/workspaces/telemetry";
import type { FlowExecution, FlowPromptEvent } from "@skaile/workspaces/types";
import * as z from "zod";
import { FlowAdapter } from "./adapter.js";
import {
  findFlowFileInRoots,
  FLOW_FILE_EXTENSIONS,
  type FlowLookupMiss,
  flowLookupRoots,
  loadFlow,
} from "./engine/index.js";
import { flowSkillVersionPins } from "./engine/node-shape.js";
import { SessionInlineSubFlowRunner } from "./inline-sub-flow-host.js";
import { SessionNodeExecutor } from "./node-executor.js";
import { driveOrchestratorTurn } from "./orchestrator-turn.js";
import { renderStimulusPrompt, type TurnStimulus } from "./prompt-fragments.js";
import { SessionSubpromptRunner } from "./subprompt-runner.js";

/**
 * Lazy `import("@skaile/workspaces/runner")` accessor. Cached on first call so
 * subsequent `runFlow` invocations don't re-evaluate the runner module.
 *
 * Dynamic ESM import is used (not `require`) so vitest and other ESM
 * bundlers honour their alias resolution — `require` would side-step the
 * configured aliases and pick up two distinct module instances when run
 * under vitest. The string-literal specifier ensures bun --compile keeps
 * the dependency (per the static-import-or-die rule in
 * connectors/CLAUDE.md).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _runnerModule: any | undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function loadRunnerModule(): Promise<any> {
  if (_runnerModule) return _runnerModule;
  _runnerModule = await import("@skaile/workspaces/runner");
  return _runnerModule;
}

/** Type-only re-export of SessionState so the runner tombstone shim still works. */
export type SessionState = import("@skaile/workspaces/runner").SessionState;

/** Maximum agent session duration (4 hours). Configurable via {@link RunOptions.sessionTimeoutMs}. */
const DEFAULT_SESSION_TIMEOUT_MS = 4 * 60 * 60 * 1000; // 4 hours

const FLOW_AGENT_OPERATION_NAMES = new Set([
  "get_available",
  "get_state",
  "get_globals",
  "build_handoff",
  "validate_node",
  "start_node",
  "request_approval",
  "request_input",
  "complete_node",
  "skip_node",
  "fail_node",
]);

/**
 * Options for {@link runFlow}.
 *
 * Wraps `FlowOrchestrator` with autonomous mode turned on by default. First-class
 * gate nodes remain mandatory human decisions in every mode.
 *
 * @docLink packages/factory-assets/flow#run-flow
 */
export interface RunOptions {
  /** The working directory for the project being orchestrated. */
  projectDir: string;
  /** Absolute path to the `.flow.yaml` or `.flow.json` definition file. */
  flowPath: string;
  /**
   * Directory containing `agent.yaml`, `SOUL.md`, `RULES.md`, `knowledge/`.
   * Resolved from `skaile.yaml` via `resolveAgentDir` when absent.
   */
  agentDir?: string;
  /**
   * Agent driver backend. Defaults to `settings.driver` (`"omp"` or `"claude-sdk"`).
   */
  driver?: string;
  /**
   * LLM provider override (e.g. `"anthropic"`, `"openrouter"`).
   * Falls back to `settings.provider` when absent.
   */
  provider?: string;
  /**
   * Model name override. Falls back to `settings.model` when absent.
   */
  model?: string;
  /**
   * Path to a specific `settings.json` to load instead of the default
   * layered resolution chain (project → parent → global → built-in defaults).
   */
  settingsFile?: string;
  /**
   * When `true`, prints the initial flow state without starting the agent
   * and returns immediately.
   */
  dryRun?: boolean;
  /**
   * Per-session timeout in milliseconds. Defaults to
   * {@link DEFAULT_SESSION_TIMEOUT_MS} (4 hours).
   */
  sessionTimeoutMs?: number;
  /** Optional human-readable label shown in the session list. */
  sessionLabel?: string;
  /** Autonomous mode for CLI runs. Defaults to true; first-class gate nodes are never bypassed. */
  autonomousMode?: boolean;
  /**
   * Called for each {@link AgentEvent} emitted during the run
   * (e.g. `text`, `tool_call`, `state_changed`).
   */
  onEvent?: (event: AgentEvent) => void;
  /**
   * Called for each diagnostic log line. Defaults to `process.stdout`.
   */
  onLog?: (line: string) => void;
}

/**
 * Options for resuming an existing CLI session.
 *
 * Reconnects the driver's conversation thread (conversation history is
 * preserved via the stored `driverSessionId`) but restarts flow execution
 * from scratch — flow state is not persisted in CLI mode. For governed,
 * resumable flow runs use a host that persists flow state (e.g. the Skaile
 * platform).
 *
 * @docLink packages/factory-assets/flow#resume-flow
 */
export interface ResumeOptions {
  /** The working directory for the project being orchestrated. */
  projectDir: string;
  /**
   * Specific run ID to resume. When absent, the current session pointer
   * at `<projectDir>/.skaile/current` is used.
   */
  sessionId?: string;
  /**
   * Agent definition directory override. Defaults to the directory stored
   * in the session file.
   */
  agentDir?: string;
  /**
   * When `true`, prints the initial flow state without starting the agent
   * and returns immediately.
   */
  dryRun?: boolean;
  /**
   * Per-session timeout in milliseconds. Defaults to
   * {@link DEFAULT_SESSION_TIMEOUT_MS} (4 hours).
   */
  sessionTimeoutMs?: number;
  /**
   * Called for each {@link AgentEvent} emitted during the run.
   */
  onEvent?: RunOptions["onEvent"];
  /**
   * Called for each diagnostic log line. Defaults to `process.stdout`.
   */
  onLog?: RunOptions["onLog"];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Start a new flow session from the CLI.
 *
 * Wraps `FlowOrchestrator` with autonomous mode turned on by default. The
 * runner remains idle between turns; first-class gates always park for a human
 * decision, including in autonomous mode. Non-CLI hosts (Skaile platform,
 * Pichi) use the orchestrator directly via the serve command surface and drive
 * gates through user actions.
 *
 * @docLink packages/factory-assets/flow#run-flow
 */
export async function runFlow(opts: RunOptions): Promise<void> {
  const runner = await loadRunnerModule();
  const {
    bootstrapRunnerLogStore,
    bootstrapCapabilityRegistry,
    CapabilityRegistry,
    createAgentSession,
    createSessionStimulusBus,
    defineCapability,
    registerCompositionCapabilities,
    resolveDriverPaths,
    newSession,
    saveSession,
    touchSession,
  } = runner;

  const flow = loadFlow(opts.flowPath);
  const agentDir = opts.agentDir ?? resolveAgentDir(opts.projectDir);
  const settings = await resolveSettings(opts.projectDir, {
    driver: opts.driver,
    provider: opts.provider,
    model: opts.model,
  });

  const session = newSession({
    flowId: flow.id,
    projectDir: opts.projectDir,
    agentDir,
    driver: settings.driver ?? "omp",
    provider: settings.provider,
    model: settings.model,
    label: opts.sessionLabel,
  });

  await saveSession(opts.projectDir, session);

  // Bootstrap structured logging — registers a process-singleton LogStore.
  // OnLogBridgeSink mirrors entries to opts.onLog when caller provides one,
  // preserving back-compat with CLI hosts that consume plain text lines.
  const wsConfigEarly = resolveSkWorkspaceConfig(opts.projectDir);
  const { dispose: disposeLogStore } = bootstrapRunnerLogStore({
    sessionId: session.runId,
    projectDir: opts.projectDir,
    yamlBlock: (wsConfigEarly as { logging?: unknown }).logging,
    onLog: opts.onLog,
  });
  const flowLog = createLogger({
    kind: "flow-connector",
    subkind: "run",
    instance: session.runId,
  });
  const log = (line: string) => flowLog.info(line);
  const capabilityRegistry = new CapabilityRegistry();
  bootstrapCapabilityRegistry(capabilityRegistry);
  const capabilityHooks = {
    sessionId: session.runId,
    composeTools: () => capabilityRegistry.composeLLMTools(),
    resolve: (name: string) =>
      capabilityRegistry.list().find((cap: { name: string }) => cap.name === name) ?? null,
    invoke: (name: string, input: unknown) =>
      capabilityRegistry.invoke(name, input, { sessionId: session.runId }),
  };

  try {
    // ── Dry run ────────────────────────────────────────────────────────────
    if (opts.dryRun) {
      const adapter = new FlowAdapter();
      const handle = await adapter.connect(
        {
          id: "flow",
          driver: "flow",
          access: "read-only" as const,
          options: { flow, seed: { runId: session.runId, startedBy: "cli" } },
        },
        {}, // flow needs no connect context
      );
      const state = adapter.getExecution(handle);
      const available = Object.values(state.nodes)
        .filter((n) => n.status === "available")
        .map((n) => n.id);
      log(`[dry-run] Flow: ${flow.name} (${flow.id})`);
      log(`[dry-run] Available: ${available.join(", ") || "(none)"}`);
      await adapter.disconnect(handle);
      return;
    }

    await validatePinnedVersions(flow, opts.projectDir, log);

    // ── Telemetry initialization ────────────────────────────────────────────
    const wsConfig = wsConfigEarly;
    const telemetry = await initTelemetry(wsConfig.telemetry);
    const flowTrace = telemetry.startTrace({
      name: `flow:${flow.id}`,
      kind: "flow_run",
      attributes: {
        "skaile.flow.id": flow.id,
        "skaile.flow.run_id": session.runId,
      },
    });

    // ── Agent session setup ─────────────────────────────────────────────────
    const driverPaths = resolveDriverPaths(settings);
    const agentSession = await createAgentSession({
      projectDir: opts.projectDir,
      agentDir: session.agentDir,
      driver: session.driver,
      model: session.model,
      provider: session.provider,
      resumeSessionId: session.driverSessionId,
      projectClaudeMd: path.join(opts.projectDir, "CLAUDE.md"),
      promptsDir: path.join(opts.projectDir, driverPaths.promptsDir),
      sessionId: session.runId,
      onLog: log,
      telemetry,
      trace: flowTrace,
      wsConfig,
      capabilities: capabilityHooks,
      capabilityRegistry,
    });
    if (agentSession.composition?.discoverableEntries.length) {
      registerCompositionCapabilities(
        capabilityRegistry,
        agentSession.composition.discoverableEntries,
      );
    }
    const { driver } = agentSession;
    const nodeExecutor = new SessionNodeExecutor({ workspaceDir: opts.projectDir });
    // Same credential path the session driver used: `resolveApiKey` against the
    // same already-resolved `settings` — not a new resolution, not a new secret
    // channel. CLI runs never set a non-default cloud transport (createAgentSession
    // above passes none), so cloud/cloudConfig/cloudSecrets are intentionally omitted.
    const subpromptRunner = new SessionSubpromptRunner({
      driverType: agentSession.driverType,
      provider: agentSession.provider,
      sessionModel: agentSession.model,
      workspaceDir: opts.projectDir,
      apiKey: resolveApiKey(agentSession.provider ?? "anthropic", settings),
    });
    let driverStarted = false;
    const updatedSession = { ...session };

    // Track driver session ID so future resumes can reconnect the SDK thread.
    driver.on("agent-event", () => {
      const runtimeSessionId = driver.runtimeSessionId;
      if (!runtimeSessionId || runtimeSessionId === updatedSession.driverSessionId) return;
      updatedSession.driverSessionId = runtimeSessionId;
      void saveSession(opts.projectDir, updatedSession).catch(() => {});
    });

    // ── FlowAdapter + stimulus bus ─────────────────────────────────────────
    //
    // Phase 4 of the flow-connector extraction: SessionStimulusBus is the sole
    // turn driver. The legacy FlowOrchestrator + onStateChange subscription
    // are gone — every turn is initiated either by the bus (after an adapter
    // mutation observed via `useBusForStimulus: true`) or by an explicit
    // `bus.signal()` call from this function (the initial flow_started turn).
    //
    // The bus's `driveTurn` callback receives the stimulus paragraph the
    // adapter rendered (not the full orchestrator prompt). It hands the turn to
    // `driveOrchestratorTurn`, which rebuilds the five-section prompt so the
    // agent sees the same flow context the legacy orchestrator produced — and
    // announces it, so a CLI consumer's `onEvent` stream carries the prompt too.
    const flowConnectorId = "flow";
    const turnTimeoutMs = opts.sessionTimeoutMs ?? DEFAULT_SESSION_TIMEOUT_MS;

    const ensureDriverStarted = async (): Promise<void> => {
      if (driverStarted) return;
      await driver.start();
      driverStarted = true;
    };

    // Same widening the `state_changed` forwarder below performs: `onEvent` is
    // typed against the bridge's driver-level union, which has no case for a
    // runtime-level event.
    const emitFlowPrompt = (event: FlowPromptEvent): void => {
      opts.onEvent?.(event as unknown as AgentEvent);
    };

    const inlineSubFlowRunner = new SessionInlineSubFlowRunner({
      resolveFlow: (flowId) => loadFlow(resolveFlowPath(flowId, opts.projectDir)),
      nodeExecutor,
      subpromptRunner,
      driveAgentTurn: async ({ flow: childFlow, execution, stimulus }) => {
        await ensureDriverStarted();
        await driveOrchestratorTurn({
          flow: childFlow,
          execution,
          stimulus,
          emit: emitFlowPrompt,
          deliver: (prompt) => awaitAgentEnd(driver, prompt, opts.onEvent, turnTimeoutMs),
        });
      },
    });

    // Forward declaration so `driveTurn` can read the live execution
    // snapshot. Assigned just below, before the bus can fire.
    let flowAdapterRef: FlowAdapter | null = null;
    let flowHandleRef: import("@skaile/workspaces/connectors").ConnectorHandle | null = null;

    const stimulusBus = createSessionStimulusBus({
      driveTurn: async (
        promptFragment: string,
        metas: Array<Record<string, unknown>>,
      ): Promise<void> => {
        // The adapter signal carries `meta.kind` per stimulus. Use that to
        // reconstruct the typed `TurnStimulus`; for compound batches, fall
        // back to `state_changed` (the union element that means "look at
        // current state and continue").
        const lastKind = metas[metas.length - 1]?.kind;
        let stimulus: TurnStimulus;
        if (lastKind === "flow_started") {
          stimulus = { kind: "flow_started" };
        } else if (
          lastKind === "user_message" &&
          typeof metas[metas.length - 1]?.text === "string"
        ) {
          const meta = metas[metas.length - 1] as Record<string, unknown>;
          stimulus = {
            kind: "user_message",
            text: String(meta.text ?? ""),
            senderId: String(meta.senderId ?? "user"),
          };
        } else {
          stimulus = { kind: "state_changed" };
        }
        // The fragment passed in by the bus is the adapter's
        // `renderStimulusPrompt(stim)` joined across coalesced signals. It is
        // discarded in favour of the structured stimulus, which
        // `driveOrchestratorTurn` rebuilds the full prompt from; that keeps the
        // prompt shape stable and the structured form already contains
        // everything the fragment encoded.
        void promptFragment;
        if (!flowAdapterRef || !flowHandleRef) {
          throw new Error("driveTurn fired before FlowAdapter was wired");
        }
        await flowAdapterRef.drainRuntimeNodes(flowHandleRef);
        const execution = flowAdapterRef.getExecution(flowHandleRef);
        if (execution.done || execution.status === "failed" || execution.status === "cancelled") {
          return;
        }
        if (!flowAdapterRef.shouldDriveAgentTurn(flowHandleRef, stimulus)) return;
        await ensureDriverStarted();
        await driveOrchestratorTurn({
          flow,
          execution,
          stimulus,
          emit: emitFlowPrompt,
          deliver: (prompt) => awaitAgentEnd(driver, prompt, opts.onEvent, turnTimeoutMs),
        });
      },
      log: flowLog,
    });

    const flowAdapter = new FlowAdapter();
    const flowHandle = await flowAdapter.connect(
      {
        id: flowConnectorId,
        driver: "flow",
        access: "read-write" as const,
        options: {
          flow,
          seed: {
            runId: session.runId,
            startedBy: "cli",
            autonomousMode: opts.autonomousMode ?? true,
          },
          stimulusBus,
          useBusForStimulus: true,
          nodeExecutor,
          subpromptRunner,
          inlineSubFlowRunner,
        },
      },
      {}, // flow needs no connect context
    );
    flowAdapterRef = flowAdapter;
    flowHandleRef = flowHandle;

    // Flow operations retain the connector's published bare protocol names;
    // this is the intentional exception to generic capability namespaces.
    for (const operation of flowAdapter.tools
      .describeOperations(flowHandle)
      .filter(({ name }) => FLOW_AGENT_OPERATION_NAMES.has(name))) {
      const shape: Record<string, z.ZodType> = {};
      for (const arg of operation.args) {
        let schema: z.ZodType;
        switch (arg.type) {
          case "boolean":
            schema = z.boolean();
            break;
          case "number":
            schema = z.number();
            break;
          case "json":
            schema = z.json();
            break;
          default:
            schema = z.string();
        }
        shape[arg.name] = arg.required ? schema : schema.optional();
      }
      capabilityRegistry.register(
        defineCapability({
          name: operation.name,
          displayName: operation.name,
          description: operation.description,
          input: z.object(shape),
          side: "agent",
          origin: { kind: "flow", flowId: flow.id },
          scope: "session",
          audience: ["llm"],
          kind: operation.accessLevel === "read" ? "query" : "effect",
          handler: async (args: Record<string, unknown>) =>
            flowAdapter.executeOp(flowHandle, operation.name, args),
        }),
        "agent",
      );
    }

    // Forward state_changed events to the caller. The bus computes a
    // turn-kicking stimulus on each adapter mutation; the host-facing
    // event stream still observes the full snapshot via onChange.
    flowAdapter.onStateChange(flowHandle, (state: FlowExecution) => {
      opts.onEvent?.({
        type: "state_changed",
        store: `flow:${state.runId}`,
        state: state as unknown as Record<string, unknown>,
      } as unknown as AgentEvent);
    });

    // ── Execute ────────────────────────────────────────────────────────────
    flowLog.info("starting flow", {
      flowId: flow.id,
      flowName: flow.name,
      runId: session.runId,
    });

    try {
      // Kick the initial flow_started turn explicitly. Subsequent turns are
      // driven by the bus signaling whenever the adapter mutates.
      const initialStimulus: TurnStimulus = { kind: "flow_started" };
      await stimulusBus.signal(flowConnectorId, {
        promptFragment: renderStimulusPrompt(initialStimulus),
        meta: { kind: initialStimulus.kind },
      });

      const finalState = flowAdapter.getExecution(flowHandle);
      if (finalState.done) {
        flowLog.info("flow complete", { flowId: flow.id, flowName: flow.name });
      } else if (finalState.status === "failed") {
        flowLog.error("flow failed", undefined, { flowId: flow.id, flowName: flow.name });
      } else {
        flowLog.warn("flow paused awaiting further stimulus", {
          flowId: flow.id,
          status: finalState.status,
          note: "CLI mode does not supply human approval/input decisions",
        });
      }
    } finally {
      driver.kill();
      const lastState = flowAdapter.getExecution(flowHandle);
      await saveSession(
        opts.projectDir,
        touchSession({
          ...updatedSession,
          driverSessionId: driver.runtimeSessionId ?? updatedSession.driverSessionId,
          status: lastState.status === "complete" ? "complete" : "paused",
        }),
      ).catch(() => {});
      await flowAdapter.disconnect(flowHandle).catch(() => {});
      await agentSession.dispose();
      telemetry.endTrace(flowTrace, {
        status: flowAdapter.getExecution(flowHandle).status === "complete" ? "ok" : "error",
      });
      await telemetry.flush().catch(() => {});
    }
  } finally {
    disposeLogStore();
  }
}

/**
 * Resume an existing session from the CLI.
 *
 * With the Phase 2 turn-based model, flow state is NOT persisted to
 * local session files — only the driver session ID is kept. The CLI
 * can resume the agent's conversation thread, but the flow execution
 * itself starts from scratch because the runner has no durable store
 * for per-run state.
 *
 * For a governed, resumable flow run, use a host that persists flow
 * state (e.g. the Skaile platform). CLI resume is retained as a
 * driver-level convenience only: we load the session, restart the
 * flow, and the SDK will reconnect to the prior conversation thread.
 *
 * @docLink packages/factory-assets/flow#resume-flow
 */
export async function resumeFlow(opts: ResumeOptions): Promise<void> {
  const runner = await loadRunnerModule();
  const { loadSession, loadSessionById, bootstrapRunnerLogStore } = runner;

  const session = opts.sessionId
    ? await loadSessionById(opts.projectDir, opts.sessionId)
    : await loadSession(opts.projectDir);

  if (!session) {
    const hint = opts.sessionId
      ? `session ${opts.sessionId}`
      : `current session in ${opts.projectDir}/.skaile`;
    throw new Error(`No session found (${hint}) — run 'start' first`);
  }

  const flowPath = resolveFlowPath(session.flowId, opts.projectDir);

  // Bootstrap a transient logger for the resume diagnostics. The inner
  // runFlow() call will register its own LogStore for the new run. Keep
  // OnLogBridgeSink wiring so callers passing onLog still see this line.
  const wsConfigEarly = resolveSkWorkspaceConfig(opts.projectDir);
  const { dispose: disposeLogStore } = bootstrapRunnerLogStore({
    sessionId: session.runId,
    projectDir: opts.projectDir,
    yamlBlock: (wsConfigEarly as { logging?: unknown }).logging,
    onLog: opts.onLog,
  });
  const flowLog = createLogger({
    kind: "flow-connector",
    subkind: "run",
    instance: session.runId,
  });
  flowLog.info("resuming session", {
    runId: session.runId,
    flowId: session.flowId,
    note: "CLI mode does not persist flow state — flow restarts from scratch; driver thread resumes",
  });

  try {
    await runFlow({
      projectDir: opts.projectDir,
      flowPath,
      agentDir: opts.agentDir ?? session.agentDir,
      driver: session.driver,
      model: session.model,
      provider: session.provider,
      sessionLabel: session.label,
      dryRun: opts.dryRun,
      sessionTimeoutMs: opts.sessionTimeoutMs,
      onEvent: opts.onEvent,
      onLog: opts.onLog,
    });
  } finally {
    disposeLogStore();
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function validatePinnedVersions(
  flow: Awaited<ReturnType<typeof loadFlow>>,
  projectDir: string,
  log: (line: string) => void,
): Promise<void> {
  const pinnedNodes = flowSkillVersionPins(flow);
  if (pinnedNodes.length === 0) return;

  const skillVersions = new Map<string, string>();
  const aiResourcesDir = findAiResourcesDir(projectDir);
  if (aiResourcesDir) {
    for (const node of pinnedNodes) {
      const skillId = node.skillId;
      if (skillVersions.has(skillId)) continue;
      const skillPath = findSkillMd(aiResourcesDir, skillId);
      if (skillPath) {
        const content = fs.readFileSync(skillPath, "utf-8");
        const fm = parseSkillFrontmatter(content);
        skillVersions.set(skillId, fm.metadata?.version ?? fm.version ?? "");
      }
    }
  }

  const checks = validateFlowVersions(pinnedNodes, skillVersions);
  const failures = checks.filter((c) => !c.satisfied);
  if (failures.length > 0) {
    const lines = failures.map(
      (f) => `  ${f.nodeId}: wants ${f.requested}, found ${f.actual || "(none)"}`,
    );
    throw new Error(`Skill version mismatch:\n${lines.join("\n")}`);
  }
  for (const c of checks.filter((c) => c.satisfied)) {
    log(`  Version OK: ${c.skillId} ${c.actual} satisfies ${c.requested}`);
  }
}

/**
 * Assert `found` sits inside one of `roots`.
 *
 * A backstop, not the primary control: `isValidAssetName` already rejects every
 * id containing a separator, and `findFlowFileInRoots` skips any candidate that
 * escapes its search dir. So on today's code this cannot fire — it is here to
 * fail loudly rather than silently if a future lookup stops enforcing
 * containment itself. Widening the accepted extensions must never widen which
 * paths are reachable, and this is the line that keeps that honest.
 */
function assertContainedInAnyRoot(found: string, roots: string[], flowId: string): void {
  const resolved = path.resolve(found);
  const inside = roots.some((root) => {
    const relative = path.relative(path.resolve(root), resolved);
    return !relative.startsWith("..") && !path.isAbsolute(relative);
  });
  if (!inside) {
    throw new Error(`Flow path escapes its lookup root: ${JSON.stringify(flowId)}`);
  }
}

/** One line per near-miss: a file that is right there but was still rejected. */
function describeMisses(misses: FlowLookupMiss[], heading: string): string {
  const seen = new Set<string>();
  const lines: string[] = [];
  for (const miss of misses) {
    if (seen.has(miss.path)) continue;
    seen.add(miss.path);
    lines.push(
      miss.reason === "id-mismatch"
        ? `  ${miss.path}: declares id ${JSON.stringify(miss.declaredId ?? "")}`
        : `  ${miss.path}: could not be parsed (${miss.error ?? "unknown error"})`,
    );
  }
  return lines.length > 0 ? `\n${heading}\n${lines.join("\n")}` : "";
}

/**
 * Resolve one flow identity through the CLI/serve host's normal lookup order —
 * the same roots and filenames `skaile run` and `flow list` use, via
 * {@link flowLookupRoots} / {@link findFlowFileInRoots}. Resolution is by the
 * **declared** `id`, not by filename.
 *
 * @throws {Error} When `flowId` is not a canonical asset name, when no file
 *   declares it, or when a match somehow resolves outside every lookup root.
 */
export function resolveFlowPath(flowId: string, projectDir: string): string {
  // Sub-flow targets and resumed session records are the two callers, so an
  // identity that is not canonical is a bug in the caller, not a missing file.
  if (!isValidAssetName(flowId)) {
    throw new Error(`Invalid canonical flow identity: ${JSON.stringify(flowId)}`);
  }

  const roots = flowLookupRoots(projectDir);
  const misses: FlowLookupMiss[] = [];
  const found = findFlowFileInRoots(flowId, roots, { onMiss: (m) => misses.push(m) });
  if (found) {
    assertContainedInAnyRoot(found, roots, flowId);
    return found;
  }

  // A file that is present but unparseable is a different problem from a missing
  // one, and "not found" is the wrong headline for it — that exact misdirection
  // is what this change exists to remove, so don't reintroduce it here.
  if (misses.length > 0 && misses.every((m) => m.reason === "unparseable")) {
    throw new Error(
      `Flow ${flowId} was found but could not be loaded.` + describeMisses(misses, "Candidates:"),
    );
  }

  const shown = roots.slice(0, 3).join(", ");
  const more = roots.length > 3 ? ", …" : "";
  // Spelled from FLOW_FILE_EXTENSIONS so the message cannot drift from the
  // candidate list the lookup actually probes.
  const accepted = FLOW_FILE_EXTENSIONS.map((ext) => `${flowId}${ext}`).join(" | ");
  throw new Error(
    `Flow not found: ${flowId}. Searched ${roots.length} root(s) (${shown || "none"}${more}) ` +
      `for ${accepted} — flat, or inside a ${flowId}/ directory.` +
      describeMisses(misses, "Near misses:"),
  );
}

/** Walk up from projectDir to find ai-assets/ */
function findAiResourcesDir(projectDir: string): string | null {
  let dir = projectDir;
  for (let i = 0; i < 6; i++) {
    const candidate = path.join(dir, "ai-assets");
    if (fs.existsSync(candidate)) return candidate;
    const parent = path.dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  return null;
}

/** Find SKILL.md for a given skill ID by walking ai-assets domains */
function findSkillMd(aiResourcesDir: string, skillId: string): string | null {
  for (const domain of fs.readdirSync(aiResourcesDir, { withFileTypes: true })) {
    if (!domain.isDirectory()) continue;
    const skillsDir = path.join(aiResourcesDir, domain.name, "skills");
    if (!fs.existsSync(skillsDir)) continue;
    const found = walkForSkill(skillsDir, skillId);
    if (found) return found;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Turn-wait helper
// ---------------------------------------------------------------------------

/**
 * Minimal driver surface needed by the bus's `driveTurn` callback. Mirrors
 * the slice the legacy `FlowOrchestrator` consumed (`prompt` + `on` +
 * `removeAllListeners`) so we can keep the wait helper private to this
 * module without pulling in a runtime dep on `@skaile/workspaces/bridge`.
 */
interface TurnDriverSurface {
  prompt(message: string): Promise<void>;
  on(event: "agent-event", handler: (event: AgentEvent) => void): void;
  removeAllListeners(event: "agent-event"): void;
}

/**
 * Send a prompt to the driver and resolve when the agent emits
 * `agent_end` (or reject on `error`). Identical contract to the legacy
 * `awaitAgentEnd` helper from `flow-orchestrator.ts`. Phase 4 lifts the
 * helper into `run-flow.ts` because the bus now owns turn driving.
 */
function awaitAgentEnd(
  driver: TurnDriverSurface,
  prompt: string,
  onEvent: ((event: AgentEvent) => void) | undefined,
  timeoutMs: number,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let settled = false;

    const done = (err?: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      driver.removeAllListeners("agent-event");
      if (err) reject(err);
      else resolve();
    };

    const timer = setTimeout(
      () => done(new Error(`Flow turn timed out after ${timeoutMs / 1000}s`)),
      timeoutMs,
    );

    driver.on("agent-event", (event: AgentEvent) => {
      onEvent?.(event);
      if (event.type === "agent_end") done();
      else if (event.type === "error") {
        const msg =
          (event as { error?: string; message?: string }).error ??
          (event as { message?: string }).message ??
          "Agent error";
        done(new Error(msg));
      }
    });

    driver.prompt(prompt).catch(done);
  });
}

function walkForSkill(dir: string, skillId: string): string | null {
  for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
    if (!entry.isDirectory()) continue;
    const full = path.join(dir, entry.name);
    const skillMd = path.join(full, "SKILL.md");
    if (fs.existsSync(skillMd)) {
      if (entry.name === skillId) return skillMd;
    } else {
      const found = walkForSkill(full, skillId);
      if (found) return found;
    }
  }
  return null;
}
